import { useMemo } from 'react';
import { type CellContext } from '@tanstack/react-table';
import { Box, Button, styled, MenuItem, type Theme } from '@mui/material';
import { Link } from 'react-router-dom';
import get from 'lodash/get';
import {
  ProductCode,
  ServiceStatus,
  type ServiceStatusInfo,
} from '../../types';
import { shouldDisplayTrainingButton } from './shouldDisplayTrainingButton';
import { WizerConnectorStatus } from '../../hooks/useGetWizerConnectorStatus';
import {
  MenuTriggerButton,
  SimpleMenu,
} from '../../../../components/Menus/SimpleMenu';
import { AgreementStatus } from '../../features/self-serve/types/types';
import { DocumentDownloader } from '../../../../components/fileUploader/DocumentDownloader';

export const RowActions = (props: CellContext<ServiceStatusInfo, unknown>) => {
  const isEligibleForFreeCtaas = get(
    props.table.options.meta,
    'isEligibleForFreeCtaas',
    false
  ) as boolean;
  const wizerConnectorStatus = get(
    props.table.options.meta,
    'wizerConnectorStatus',
    WizerConnectorStatus.NotActivated
  ) as WizerConnectorStatus;

  const isTrainingButtonVisible = shouldDisplayTrainingButton({
    row: props.row,
    wizerConnectorStatus,
    isEligibleForFreeCtaas,
  });

  const allAgreements = get(props.table.options.meta, 'agreements', []);

  const agreements = useMemo(() => {
    return allAgreements.filter((a) => {
      return (
        a.status === AgreementStatus.SIGNED &&
        a.product.code === props.row.original.product.code
      );
    });
  }, [props.row.original?.product?.code, allAgreements]);

  return (
    <Box
      gap={1}
      display="flex"
      alignItems="center"
      className="button-container"
    >
      <Link
        to={`/customer/resilience/catalog/services/${props.row.original.productFamily.id}/pricing`}
      >
        <OutlineButton variant="outlined" size="small" color="primary">
          Plans
        </OutlineButton>
      </Link>
      {props.row.original.product.code === ProductCode.VRA_PRO &&
        props.row.original.status === ServiceStatus.Activated && (
          <Link to="/customer/resilience/vra/dashboard?page=0&size=10">
            <OutlineButton variant="outlined" size="small" color="primary">
              Go to VRA
            </OutlineButton>
          </Link>
        )}
      {isTrainingButtonVisible && (
        <Link to="/customer/training">
          <OutlineButton variant="outlined" size="small" color="primary">
            Go to Training
          </OutlineButton>
        </Link>
      )}
      {agreements.length > 0 && (
        <SimpleMenu
          id="service-table-row-actions-menu"
          anchor={<MenuTriggerButton />}
        >
          {agreements.map((agreement, idx) => {
            return (
              <DocumentDownloader
                key={agreement.agreementId}
                id={agreement.agreementId}
                name={`Agreement ${idx + 1}`}
                type="pdf"
              >
                {({ handleDocumentDownload }) => (
                  <MenuItem onClick={handleDocumentDownload}>
                    Download Agreement {idx + 1}
                  </MenuItem>
                )}
              </DocumentDownloader>
            );
          })}
        </SimpleMenu>
      )}
    </Box>
  );
};

const OutlineButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  border: `1px solid ${theme.palette.common.azureBlue}`,
  color: theme.palette.common.azureBlue,
  fontSize: '1rem',
  fontWeight: 600,
  letterSpacing: '1.25px',
  textAlign: 'center',
  textTransform: 'uppercase',

  ':hover': {
    background: theme.palette.common.surfaceActionHover2,
    border: `1px solid ${theme.palette.common.azureBlue}`,
    color: theme.palette.common.textActionHover,
  },
}));
